import { useState } from "react";
import { X, ArrowRight, ArrowLeft, Loader2, Sparkles, Bookmark, Check } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const QuizModal = ({ isOpen, onClose }) => {
  const { user, login } = useAuth();
  const [step, setStep] = useState(0); // 0 = intro, 1-10 = questions, 11 = info, 12 = result
  const [answers, setAnswers] = useState({});
  const [userInfo, setUserInfo] = useState({ name: "", email: "", age_range: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [personalizedMessage, setPersonalizedMessage] = useState(null);

  const questions = [
    {
      id: "q1",
      question: "How would you describe your energy levels most days?",
      options: [
        { value: 1, label: "Steady throughout the day" },
        { value: 2, label: "Some ups and downs but manageable" },
        { value: 3, label: "Significant crashes, especially afternoon" },
        { value: 4, label: "Exhausted from the moment I wake up" }
      ]
    },
    {
      id: "q2",
      question: "How is your sleep quality?",
      options: [
        { value: 1, label: "I sleep well through the night" },
        { value: 2, label: "Occasional wake-ups but fall back asleep" },
        { value: 3, label: "Regular 2-4am wake-ups, hard to fall back asleep" },
        { value: 4, label: "Insomnia most nights" }
      ]
    },
    {
      id: "q3",
      question: "How would you describe your mental clarity?",
      options: [
        { value: 1, label: "Sharp and focused as usual" },
        { value: 2, label: "Slightly foggy at times" },
        { value: 3, label: "Frequent brain fog affecting work" },
        { value: 4, label: "Can't focus, forget words constantly" }
      ]
    },
    {
      id: "q4",
      question: "How stable are your moods?",
      options: [
        { value: 1, label: "Generally stable and balanced" },
        { value: 2, label: "Minor mood fluctuations" },
        { value: 3, label: "Noticeable anxiety or irritability" },
        { value: 4, label: "Extreme mood swings, rage, or crying spells" }
      ]
    },
    {
      id: "q5",
      question: "How connected do you feel to your sense of purpose?",
      options: [
        { value: 1, label: "Clear about who I am and where I'm going" },
        { value: 2, label: "Mostly sure but questioning some things" },
        { value: 3, label: "Feeling lost about my identity" },
        { value: 4, label: "Completely disconnected from myself" }
      ]
    },
    {
      id: "q6",
      question: "Do you experience afternoon energy crashes?",
      options: [
        { value: 1, label: "Rarely or never" },
        { value: 2, label: "Occasionally" },
        { value: 3, label: "Most days" },
        { value: 4, label: "Every single day" }
      ]
    },
    {
      id: "q7",
      question: "How often do you wake up between 2-4am?",
      options: [
        { value: 1, label: "Rarely or never" },
        { value: 2, label: "Once or twice a week" },
        { value: 3, label: "3-5 times a week" },
        { value: 4, label: "Every night" }
      ]
    },
    {
      id: "q8",
      question: "How is your concentration at work?",
      options: [
        { value: 1, label: "As good as ever" },
        { value: 2, label: "Slightly reduced" },
        { value: 3, label: "Noticeably impaired" },
        { value: 4, label: "Severely affecting my performance" }
      ]
    },
    {
      id: "q9",
      question: "How often do you experience unexplained anxiety or irritability?",
      options: [
        { value: 1, label: "Rarely" },
        { value: 2, label: "A few times a month" },
        { value: 3, label: "Weekly" },
        { value: 4, label: "Daily" }
      ]
    },
    {
      id: "q10",
      question: "How confident do you feel in making decisions?",
      options: [
        { value: 1, label: "Very confident" },
        { value: 2, label: "Mostly confident" },
        { value: 3, label: "Often second-guessing myself" },
        { value: 4, label: "Paralyzed by decisions" }
      ]
    }
  ];

  const ageRanges = ["40-45", "46-50", "51-55", "56-60", "60+"];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    // Advance to next question or to user info form (step 11)
    if (step <= questions.length) {
      setTimeout(() => setStep(step + 1), 300);
    }
  };

  const handleSubmit = async () => {
    if (!userInfo.name || !userInfo.email || !userInfo.age_range) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/quiz/submit`, {
        name: userInfo.name,
        email: userInfo.email,
        age_range: userInfo.age_range,
        answers
      });
      setResult(response.data);
      setQuizId(response.data.quiz_id);
      setStep(12);
      // Fetch personalized AI recommendation (fires-and-forgets updates result state)
      axios.post(`${API}/quiz/personalize`, {
        result_type: response.data.result_type,
        name: userInfo.name,
      }).then((r) => {
        if (r.data.success && r.data.message) {
          setPersonalizedMessage(r.data.message);
        }
      }).catch(() => {});
    } catch (error) {
      console.error("Quiz submission error:", error);
      toast.error("Unable to submit quiz. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveToAccount = async () => {
    if (!user) {
      // Not logged in - redirect to Google sign in
      login();
      return;
    }
    if (!quizId || isSaved) return;
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/auth/save-quiz`,
        { quiz_id: quizId },
        { withCredentials: true }
      );
      setIsSaved(true);
      toast.success("Saved to your account!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Couldn't save. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const resetQuiz = () => {
    setStep(0);
    setAnswers({});
    setUserInfo({ name: "", email: "", age_range: "" });
    setResult(null);
    setQuizId(null);
    setIsSaved(false);
    setIsSaving(false);
    setPersonalizedMessage(null);
  };

  const handleClose = () => {
    resetQuiz();
    onClose();
  };

  if (!isOpen) return null;

  const progress = step === 0 ? 0 : step <= 10 ? (step / 10) * 100 : 100;

  return (
    <div className="modal-backdrop" onClick={handleClose} data-testid="quiz-modal">
      <div
        className="bg-[#F9F8F6] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-[#E3DAC9] p-6 rounded-t-3xl">
          <div className="flex items-center justify-between mb-4">
            <span className="overline">Free Assessment</span>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-[#E3DAC9] rounded-full transition-colors"
              data-testid="quiz-close-btn"
            >
              <X className="w-5 h-5 text-[#4A5543]" />
            </button>
          </div>
          {step > 0 && step <= 11 && (
            <div className="progress-bar">
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Intro */}
          {step === 0 && (
            <div className="text-center animate-fade-in">
              <Sparkles className="w-12 h-12 text-[#C5A059] mx-auto mb-6" />
              <h2 className="font-heading text-3xl text-[#2C3524] mb-4">
                Discover Your Perimenopause Reset Type
              </h2>
              <p className="text-[#4A5543] mb-8 max-w-md mx-auto">
                Answer 10 quick questions to identify your unique hormonal pattern 
                and get a personalized roadmap to transformation.
              </p>
              <button
                onClick={() => setStep(1)}
                className="btn-primary group"
                data-testid="quiz-start-btn"
              >
                Start the Quiz
                <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <p className="text-sm text-[#4A5543]/70 mt-4">Takes less than 3 minutes</p>
            </div>
          )}

          {/* Questions */}
          {step >= 1 && step <= 10 && (
            <div className="animate-fade-in">
              <p className="text-sm text-[#C5A059] mb-2">Question {step} of 10</p>
              <h3 className="font-heading text-2xl text-[#2C3524] mb-6">
                {questions[step - 1].question}
              </h3>
              <div className="space-y-3">
                {questions[step - 1].options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(questions[step - 1].id, option.value)}
                    className={`quiz-option w-full text-left ${
                      answers[questions[step - 1].id] === option.value ? "selected" : ""
                    }`}
                    data-testid={`quiz-option-${option.value}`}
                  >
                    <span className="text-[#2C3524]">{option.label}</span>
                  </button>
                ))}
              </div>
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-2 text-[#4A5543] mt-6 hover:text-[#C5A059]"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
            </div>
          )}

          {/* User Info */}
          {step === 11 && (
            <div className="animate-fade-in">
              <h3 className="font-heading text-2xl text-[#2C3524] mb-2">
                Almost there!
              </h3>
              <p className="text-[#4A5543] mb-6">
                Enter your details to receive your personalized results and reset plan.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#4A5543] mb-2">Your Name</label>
                  <input
                    type="text"
                    value={userInfo.name}
                    onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                    className="input-field"
                    placeholder="Enter your name"
                    data-testid="quiz-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#4A5543] mb-2">Email Address</label>
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    className="input-field"
                    placeholder="Enter your email"
                    data-testid="quiz-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#4A5543] mb-2">Age Range</label>
                  <div className="flex flex-wrap gap-2">
                    {ageRanges.map((range) => (
                      <button
                        key={range}
                        onClick={() => setUserInfo({ ...userInfo, age_range: range })}
                        className={`px-4 py-2 rounded-full border transition-all ${
                          userInfo.age_range === range
                            ? "bg-[#C5A059] text-white border-[#C5A059]"
                            : "border-[#E3DAC9] text-[#4A5543] hover:border-[#C5A059]"
                        }`}
                        data-testid={`quiz-age-${range}`}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary w-full mt-8"
                data-testid="quiz-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Analyzing...
                  </>
                ) : (
                  "Get My Results"
                )}
              </button>
              <button
                onClick={() => setStep(10)}
                className="flex items-center gap-2 text-[#4A5543] mt-4 mx-auto hover:text-[#C5A059]"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>
          )}

          {/* Results */}
          {step === 12 && result && (
            <div className="text-center animate-scale-in">
              <div className="w-16 h-16 rounded-full bg-[#C5A059]/20 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-[#C5A059]" />
              </div>
              <p className="overline mb-2">Your Reset Type</p>
              <h2 className="font-heading text-3xl text-[#2C3524] mb-4">
                {result.result_details.title}
              </h2>
              <p className="text-[#4A5543] mb-6 max-w-md mx-auto">
                {result.result_details.description}
              </p>
              <div className="bg-[#F2F4F0] rounded-2xl p-6 mb-6">
                <p className="text-sm text-[#C5A059] font-medium mb-1">Your Primary Focus Area</p>
                <p className="font-heading text-xl text-[#2C3524]">
                  {result.result_details.focus}
                </p>
              </div>

              {/* AI-Personalized message from Ivana */}
              <div
                className="text-left bg-white border border-[#E3DAC9] rounded-2xl p-6 mb-6"
                data-testid="ai-personalized-message"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-[#C5A059]" strokeWidth={2} />
                  <span className="overline text-xs">A note from Ivana</span>
                </div>
                {personalizedMessage ? (
                  <div className="text-[#2C3524] font-light leading-relaxed whitespace-pre-wrap text-sm animate-fade-in">
                    {personalizedMessage}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-[#4A5543]/60 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-[#C5A059]" />
                    Ivana is writing a personal note for you...
                  </div>
                )}
              </div>

              <p className="text-sm text-[#4A5543] mb-6">
                Check your email for your personalized reset plan and next steps.
              </p>

              {/* Save to account button */}
              <button
                onClick={handleSaveToAccount}
                disabled={isSaving || isSaved}
                className={`w-full mb-3 py-3 rounded-full font-medium transition-all flex items-center justify-center gap-2 ${
                  isSaved
                    ? "bg-green-100 text-green-700 border-2 border-green-200"
                    : "border-2 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                }`}
                data-testid="save-quiz-btn"
              >
                {isSaved ? (
                  <>
                    <Check className="w-4 h-4" />
                    Saved to your account
                  </>
                ) : isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Bookmark className="w-4 h-4" />
                    {user ? "Save to my account" : "Sign in to save results"}
                  </>
                )}
              </button>

              <button onClick={handleClose} className="btn-primary w-full">
                Explore Programs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizModal;
